import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Package,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Truck,
  Warehouse,
  ClipboardCheck,
  Calendar,
  Eye,
  Edit,
  FileText,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  pending_qc: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
};

export const GoodsReceipt = () => {const { t } = useTranslation("procurement");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/procurement/goods-receipts`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch goods receipts');
      const data = await response.json();
      setReceipts(data);
    } catch (err) {
      toast.error('Failed to load goods receipts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [activeTab]);

  const filteredReceipts = receipts.filter((grn) => {
    if (!searchTerm) return true;
    return grn.gr_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grn.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grn.po_number?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: receipts.length,
    completed: receipts.filter((g) => g.status === 'completed').length,
    partial: receipts.filter((g) => g.status === 'partial').length,
    pending: receipts.filter((g) => g.status === 'pending' || g.status === 'pending_qc').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="procurement-goods-receipt">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("goodsReceipt", "Goods Receipt")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("recordAndTrack", "Record and track incoming shipments")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReceipts} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button
            className="gap-2 bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate('/procurement/goods-receipt/new')}>
            
            <Plus className="h-4 w-4" />{t("newReceipt", "New Receipt")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalReceipts", "Total Receipts")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("completed", "Completed")}</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("partial", "Partial")}</p>
                <p className="text-2xl font-bold">{stats.partial}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="completed">{t("completed", "Completed")}</TabsTrigger>
                <TabsTrigger value="partial">{t("partial", "Partial")}</TabsTrigger>
                <TabsTrigger value="pending_qc">{t("pendingQc", "Pending QC")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search receipts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("grNumber", "GR Number")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("poReference", "PO Reference")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("receivedDate", "Received Date")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("qc", "QC")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredReceipts.map((grn) =>
                <tr key={grn.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm font-medium">{grn.gr_number}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-orange-600">{grn.po_number}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{grn.vendor_name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {grn.received_date}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="font-medium">{grn.total_items}</span>
                    </td>
                    <td className="py-4 px-4 text-right font-mono">
                      ${grn.total_value?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={
                    grn.quality_check === 'passed' ? 'bg-emerald-100 text-emerald-700' :
                    grn.quality_check === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                    }>
                        <ClipboardCheck className="h-3 w-3 mr-1" />
                        {grn.quality_check}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={statusColors[grn.status] || statusColors.pending}>
                        {grn.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/procurement/goods-receipt/${grn.id}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {grn.status !== 'completed' &&
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/procurement/goods-receipt/edit/${grn.id}`)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredReceipts.length === 0 &&
          <div className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noReceiptsFound", "No receipts found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};